import { useMapGetter } from 'dashboard/composables/store';
import { computed, h, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import ChannelLeaf from './ChannelLeaf.vue';
import { useStore } from 'vuex';

export function getContentSidebar() {
  const { accountScopedRoute } = useAccount();
  const { t } = useI18n();
  const store = useStore();

  const inboxes = useMapGetter('inboxes/getInboxes');
  const labels = useMapGetter('labels/getLabelsOnSidebar');
  const teams = useMapGetter('teams/getMyTeams');
  const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
  const conversationCustomViews = useMapGetter(
    'customViews/getConversationCustomViews'
  );

  const isKanbanEnabled = computed(() => {
    const accountId = store.state.route?.params?.accountId;
    if (!accountId) return false;

    return store.getters['accounts/isFeatureEnabledonAccount'](
      accountId,
      'kanban'
    );
  });

  onMounted(() => {
    store.dispatch('labels/get');
    store.dispatch('inboxes/get');
    store.dispatch('notifications/unReadCount');
    store.dispatch('teams/get');
    store.dispatch('attributes/get');
    store.dispatch('customViews/get', 'conversation');
    store.dispatch('customViews/get', 'contact');
  });

  const sortedInboxes = computed(() =>
    inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
  );

  const newReportRoutes = () => [
    {
      name: 'Reports Agent',
      label: t('SIDEBAR.REPORTS_AGENT'),
      to: accountScopedRoute('agent_reports_index'),
      activeOn: ['agent_reports_show'],
    },
    {
      name: 'Reports Label',
      label: t('SIDEBAR.REPORTS_LABEL'),
      to: accountScopedRoute('label_reports_index'),
    },
    {
      name: 'Reports Inbox',
      label: t('SIDEBAR.REPORTS_INBOX'),
      to: accountScopedRoute('inbox_reports_index'),
      activeOn: ['inbox_reports_show'],
    },
    {
      name: 'Reports Team',
      label: t('SIDEBAR.REPORTS_TEAM'),
      to: accountScopedRoute('team_reports_index'),
      activeOn: ['team_reports_show'],
    },
  ];

  const reportRoutes = computed(() => newReportRoutes());

  const menuItems = computed(() => {
    const baseItems = [
      {
        name: 'Dashboard',
        label: t('SIDEBAR.HOME_DASHBOARD'),
        icon: 'i-lucide-chart-no-axes-column',
        to: accountScopedRoute('home-dashboard'),
        activeOn: ['home-dashboard'],
      },
      {
        name: 'Inbox',
        label: t('SIDEBAR.INBOX'),
        icon: 'i-lucide-inbox',
        to: accountScopedRoute('inbox_view'),
        activeOn: ['inbox_view', 'inbox_view_conversation'],
        getterKeys: {
          badge: 'notifications/getHasUnreadNotifications',
        },
      },
    ];

    if (isKanbanEnabled.value) {
      baseItems.push({
        name: 'Kanban',
        label: t('SIDEBAR.KANBAN'),
        icon: 'i-lucide-kanban',
        to: accountScopedRoute('kanban'),
        activeOn: ['kanban'],
      });
    }

    baseItems.push(
      {
        name: 'Conversation',
        label: t('SIDEBAR.CONVERSATIONS'),
        icon: 'i-lucide-message-circle',
        children: [
          {
            name: 'All',
            label: t('SIDEBAR.ALL_CONVERSATIONS'),
            activeOn: ['inbox_conversation'],
            to: accountScopedRoute('home'),
          },
          {
            name: 'Mentions',
            label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
            activeOn: ['conversation_through_mentions'],
            to: accountScopedRoute('conversation_mentions'),
          },
          {
            name: 'Unattended',
            activeOn: ['conversation_through_unattended'],
            label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
            to: accountScopedRoute('conversation_unattended'),
          },
          {
            name: 'Folders',
            label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
            icon: 'i-lucide-folder',
            activeOn: ['conversations_through_folders'],
            children: conversationCustomViews.value.map(view => ({
              name: `${view.name}-${view.id}`,
              label: view.name,
              to: accountScopedRoute('folder_conversations', { id: view.id }),
            })),
          },
          {
            name: 'Teams',
            label: t('SIDEBAR.TEAMS'),
            icon: 'i-lucide-users',
            activeOn: ['conversations_through_team'],
            children: teams.value.map(team => ({
              name: `${team.name}-${team.id}`,
              label: team.name,
              to: accountScopedRoute('team_conversations', { teamId: team.id }),
            })),
          },
          {
            name: 'Channels',
            label: t('SIDEBAR.CHANNELS'),
            icon: 'i-lucide-mailbox',
            activeOn: ['conversation_through_inbox'],
            children: sortedInboxes.value.map(inbox => ({
              name: `${inbox.name}-${inbox.id}`,
              label: inbox.name,
              to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
              component: leafProps =>
                h(ChannelLeaf, {
                  label: leafProps.label,
                  active: leafProps.active,
                  inbox,
                }),
            })),
          },
          {
            name: 'Labels',
            label: t('SIDEBAR.LABELS'),
            icon: 'i-lucide-tag',
            activeOn: ['conversations_through_label'],
            children: labels.value.map(label => ({
              name: `${label.title}-${label.id}`,
              label: label.title,
              icon: h('span', {
                class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
                style: { backgroundColor: label.color },
              }),
              to: accountScopedRoute('label_conversations', {
                label: label.title,
              }),
            })),
          },
        ],
      },
      {
        name: 'Captain',
        icon: 'i-woot-captain',
        label: t('SIDEBAR.CAPTAIN'),
        children: [
          {
            name: 'Assistants',
            label: t('SIDEBAR.CAPTAIN_ASSISTANTS'),
            to: accountScopedRoute('captain_assistants_index'),
          },
          {
            name: 'Documents',
            label: t('SIDEBAR.CAPTAIN_DOCUMENTS'),
            to: accountScopedRoute('captain_documents_index'),
          },
          {
            name: 'Responses',
            label: t('SIDEBAR.CAPTAIN_RESPONSES'),
            to: accountScopedRoute('captain_responses_index'),
          },
        ],
      },
      {
        name: 'Contacts',
        label: t('SIDEBAR.CONTACTS'),
        icon: 'i-lucide-contact',
        children: [
          {
            name: 'All Contacts',
            label: t('SIDEBAR.ALL_CONTACTS'),
            to: accountScopedRoute(
              'contacts_dashboard_index',
              {},
              { page: 1, search: undefined }
            ),
            activeOn: ['contacts_dashboard_index', 'contacts_edit'],
          },
          {
            name: 'Active',
            label: t('SIDEBAR.ACTIVE'),
            to: accountScopedRoute('contacts_dashboard_active'),
            activeOn: ['contacts_dashboard_active'],
          },
          {
            name: 'Segments',
            icon: 'i-lucide-group',
            label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
            children: contactCustomViews.value.map(view => ({
              name: `${view.name}-${view.id}`,
              label: view.name,
              to: accountScopedRoute(
                'contacts_dashboard_segments_index',
                { segmentId: view.id },
                { page: 1 }
              ),
              activeOn: [
                'contacts_dashboard_segments_index',
                'contacts_edit_segment',
              ],
            })),
          },
          {
            name: 'Tagged With',
            icon: 'i-lucide-tag',
            label: t('SIDEBAR.TAGGED_WITH'),
            children: labels.value.map(label => ({
              name: `${label.title}-${label.id}`,
              label: label.title,
              icon: h('span', {
                class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
                style: { backgroundColor: label.color },
              }),
              to: accountScopedRoute(
                'contacts_dashboard_labels_index',
                { label: label.title },
                { page: 1, search: undefined }
              ),
              activeOn: [
                'contacts_dashboard_labels_index',
                'contacts_edit_label',
              ],
            })),
          },
        ],
      },
      {
        name: 'Reports',
        label: t('SIDEBAR.REPORTS'),
        icon: 'i-lucide-chart-spline',
        children: [
          {
            name: 'Report Overview',
            label: t('SIDEBAR.REPORTS_OVERVIEW'),
            to: accountScopedRoute('account_overview_reports'),
          },
          {
            name: 'Report Conversation',
            label: t('SIDEBAR.REPORTS_CONVERSATION'),
            to: accountScopedRoute('conversation_reports'),
          },
          ...reportRoutes.value,
          {
            name: 'Reports CSAT',
            label: t('SIDEBAR.CSAT'),
            to: accountScopedRoute('csat_reports'),
          },
          {
            name: 'Reports SLA',
            label: t('SIDEBAR.REPORTS_SLA'),
            to: accountScopedRoute('sla_reports'),
          },
          {
            name: 'Reports Bot',
            label: t('SIDEBAR.REPORTS_BOT'),
            to: accountScopedRoute('bot_reports'),
          },
        ],
      },
      {
        name: 'Campaigns',
        label: t('SIDEBAR.CAMPAIGNS'),
        icon: 'i-lucide-megaphone',
        children: [
          {
            name: 'Live chat',
            label: t('SIDEBAR.LIVE_CHAT'),
            to: accountScopedRoute('campaigns_livechat_index'),
          },
          {
            name: 'SMS',
            label: t('SIDEBAR.SMS'),
            to: accountScopedRoute('campaigns_sms_index'),
          },
        ],
      },
      {
        name: 'Portals',
        label: t('SIDEBAR.HELP_CENTER.TITLE'),
        icon: 'i-lucide-library-big',
        children: [
          {
            name: 'Articles',
            label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
            activeOn: [
              'portals_articles_index',
              'portals_articles_new',
              'portals_articles_edit',
            ],
            to: accountScopedRoute('portals_index', {
              navigationPath: 'portals_articles_index',
            }),
          },
          {
            name: 'Categories',
            label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
            activeOn: [
              'portals_categories_index',
              'portals_categories_articles_index',
              'portals_categories_articles_edit',
            ],
            to: accountScopedRoute('portals_index', {
              navigationPath: 'portals_categories_index',
            }),
          },
          {
            name: 'Locales',
            label: t('SIDEBAR.HELP_CENTER.LOCALES'),
            activeOn: ['portals_locales_index'],
            to: accountScopedRoute('portals_index', {
              navigationPath: 'portals_locales_index',
            }),
          },
          {
            name: 'Settings',
            label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
            activeOn: ['portals_settings_index'],
            to: accountScopedRoute('portals_index', {
              navigationPath: 'portals_settings_index',
            }),
          },
        ],
      },
      {
        name: 'Settings',
        label: t('SIDEBAR.SETTINGS'),
        icon: 'i-lucide-bolt',
        children: [
          {
            name: 'Settings Account Settings',
            label: t('SIDEBAR.ACCOUNT_SETTINGS'),
            icon: 'i-lucide-briefcase',
            to: accountScopedRoute('general_settings_index'),
          },
          {
            name: 'Settings Agents',
            label: t('SIDEBAR.AGENTS'),
            icon: 'i-lucide-square-user',
            to: accountScopedRoute('agent_list'),
          },
          {
            name: 'Settings Teams',
            label: t('SIDEBAR.TEAMS'),
            icon: 'i-lucide-users',
            to: accountScopedRoute('settings_teams_list'),
          },
          {
            name: 'Settings Inboxes',
            label: t('SIDEBAR.INBOXES'),
            icon: 'i-lucide-inbox',
            to: accountScopedRoute('settings_inbox_list'),
          },
          {
            name: 'Settings Labels',
            label: t('SIDEBAR.LABELS'),
            icon: 'i-lucide-tags',
            to: accountScopedRoute('labels_list'),
          },
          {
            name: 'Settings Custom Attributes',
            label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
            icon: 'i-lucide-code',
            to: accountScopedRoute('attributes_list'),
          },
          {
            name: 'Settings Automation',
            label: t('SIDEBAR.AUTOMATION'),
            icon: 'i-lucide-workflow',
            to: accountScopedRoute('automation_list'),
          },
          {
            name: 'Settings Agent Bots',
            label: t('SIDEBAR.AGENT_BOTS'),
            icon: 'i-lucide-bot',
            to: accountScopedRoute('agent_bots'),
          },
          {
            name: 'Settings Macros',
            label: t('SIDEBAR.MACROS'),
            icon: 'i-lucide-toy-brick',
            to: accountScopedRoute('macros_wrapper'),
          },
          {
            name: 'Settings Canned Responses',
            label: t('SIDEBAR.CANNED_RESPONSES'),
            icon: 'i-lucide-message-square-quote',
            to: accountScopedRoute('canned_list'),
          },
          {
            name: 'Settings Integrations',
            label: t('SIDEBAR.INTEGRATIONS'),
            icon: 'i-lucide-blocks',
            to: accountScopedRoute('settings_applications'),
          },
          {
            name: 'Settings Audit Logs',
            label: t('SIDEBAR.AUDIT_LOGS'),
            icon: 'i-lucide-briefcase',
            to: accountScopedRoute('auditlogs_list'),
          },
          {
            name: 'Settings Custom Roles',
            label: t('SIDEBAR.CUSTOM_ROLES'),
            icon: 'i-lucide-shield-plus',
            to: accountScopedRoute('custom_roles_list'),
          },
          {
            name: 'Settings Sla',
            label: t('SIDEBAR.SLA'),
            icon: 'i-lucide-clock-alert',
            to: accountScopedRoute('sla_list'),
          },
          {
            name: 'Settings Billing',
            label: t('SIDEBAR.BILLING'),
            icon: 'i-lucide-credit-card',
            to: accountScopedRoute('billing_settings_index'),
          },
        ],
      }
    );

    return baseItems;
  });

  return menuItems;
}
